/**
 * Workflow Selector Component
 */

import React from 'react';
import { Autocomplete, TextField, Box, Typography, Chip } from '@mui/material';
import { useWorkflowDefinitions } from '../hooks/useWorkflowChaining';

interface WorkflowSelectorProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
}

export const WorkflowSelector: React.FC<WorkflowSelectorProps> = ({
  value,
  onChange,
  error,
  helperText,
}) => {
  const { data: workflowsData, isLoading } = useWorkflowDefinitions();

  // Handle both array response and wrapped response (e.g., { items: [...] })
  const workflows = Array.isArray(workflowsData)
    ? workflowsData
    : (workflowsData as any)?.items ?? [];

  const selectedWorkflow = workflows.find((w: any) => w.definitionId === value || w.name === value);

  return (
    <Autocomplete
      value={selectedWorkflow || null}
      onChange={(_, newValue) => onChange(newValue?.definitionId || '')}
      options={workflows}
      getOptionLabel={(option) => option.name}
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Workflow"
          error={error}
          helperText={helperText || 'Select a workflow to start when the event is triggered'}
          required
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                {option.name}
              </Typography>
              <Chip
                label={`v${option.version}`}
                size="small"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
              {option.isPublished && (
                <Chip
                  label="Published"
                  size="small"
                  color="success"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              )}
            </Box>
            {option.description && (
              <Typography variant="caption" color="text.secondary">
                {option.description}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    />
  );
};

